/**
 * Shared surface styles for text-like form controls (Input, Textarea, DatePicker, …).
 *
 * Focus = thin brand border + soft halo (Airtable) — never a hard blue ring.
 * Explicitly zero Tailwind/forms rings (`ring-0` / `focus:ring-0`) so product apps
 * that still load `@tailwindcss/forms` don't stack a dark-blue focus chrome on top.
 */
export const controlSurfaceClass =
  'w-full rounded-ds-md border border-ds-border bg-ds-surface text-ds-sm text-ds-text ' +
  'placeholder:text-ds-disabled hover:border-ds-border-strong ' +
  'outline-none ring-0 ' +
  'focus:border-ds-focus focus:outline-none focus:ring-0 focus:shadow-[var(--ds-focus-ring)] ' +
  'focus-visible:border-ds-focus focus-visible:outline-none focus-visible:ring-0 ' +
  'focus-visible:shadow-[var(--ds-focus-ring)] ' +
  'disabled:cursor-not-allowed disabled:bg-ds-subtle disabled:text-ds-disabled ' +
  'transition-[border-color,box-shadow,background-color] duration-ds-fast ease-ds';

/**
 * Composite control shell (Combobox, MultiSelect): one outer border only.
 * Focus is driven by `focus-within` because the real focus target is an inner input.
 * Do not put `controlSurfaceClass` on both shell and inner input — that double-boxes.
 */
export const controlShellClass =
  'w-full rounded-ds-md border border-ds-border bg-ds-surface text-ds-sm text-ds-text ' +
  'hover:border-ds-border-strong ' +
  'outline-none ring-0 ' +
  'focus-within:border-ds-focus focus-within:shadow-[var(--ds-focus-ring)] ' +
  'transition-[border-color,box-shadow,background-color] duration-ds-fast ease-ds';

/**
 * Invalid / validation-error control chrome — danger border only (no red fill).
 * Prefer the `invalid` prop on `Input` / `Textarea` / `Select` over ad-hoc
 * `border-ds-danger` classes so focus/hover stay consistent.
 */
export const controlInvalidClass =
  'border-ds-danger hover:border-ds-danger ' +
  'focus:border-ds-danger focus:shadow-none ' +
  'focus-visible:border-ds-danger focus-visible:shadow-none ' +
  'focus-within:border-ds-danger focus-within:shadow-none';

/** Active/open composite shell — same quiet focus (kept for explicit open state). */
export const controlFocusActiveClass =
  'border-ds-focus shadow-[var(--ds-focus-ring)]';

/**
 * Inner field of a composite shell (combobox search input, multi-select typeahead).
 * No border/ring of its own — the shell owns chrome so we never get a double outline.
 */
export const controlShellInputClass =
  'min-w-0 flex-1 border-0 bg-transparent p-0 text-ds-sm text-ds-text shadow-none outline-none ring-0 ' +
  'placeholder:text-ds-disabled ' +
  'focus:border-0 focus:outline-none focus:ring-0 focus:shadow-none ' +
  'focus-visible:outline-none focus-visible:ring-0 focus-visible:shadow-none ' +
  'disabled:cursor-not-allowed disabled:opacity-60 ' +
  'selection:bg-ds-brand-subtle selection:text-ds-text';
